import { useEffect, useRef, useState } from 'react';
import { Building2, Users } from 'lucide-react';
import { api } from '../lib/api';
import { ImproveTextButton } from './ImproveTextButton';

type Target = { id: string; name: string; kind: 'user' | 'team' | 'department' };

/**
 * Textarea con autocomplete de @menciones: al escribir "@" sugiere USUARIOS y EQUIPOS y al
 * elegir inserta "@Nombre ". El backend (parseMentions) matchea el nombre completo y notifica
 * al usuario, o a TODOS los integrantes del equipo. Controlado (value/onChange) como un textarea.
 */
export function MentionTextarea({ value, onChange, placeholder, minHeight = 40, style, onEnter, onPaste }: {
  value: string; onChange: (v: string) => void; placeholder?: string; minHeight?: number; style?: React.CSSProperties;
  onEnter?: () => void; // Enter (sin shift) con el autocomplete cerrado → enviar
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void; // pegar una captura → adjunto
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [targets, setTargets] = useState<Target[]>([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [sel, setSel] = useState(0);

  useEffect(() => {
    api.get<{ users: Array<{ id: string; name: string }>; teams: Array<{ id: string; name: string }>; departments?: Array<{ id: string; name: string }>; groups?: Array<{ id: string; name: string }> }>('/api/team/mention-targets')
      .then((r) => setTargets([
        // Grupos derivados (@Management = managers sin depto): se pintan como un team más.
        ...(r.groups || []).map((g) => ({ ...g, kind: 'team' as const })),
        ...(r.departments || []).map((d) => ({ ...d, kind: 'department' as const })),
        ...(r.teams || []).map((t) => ({ ...t, kind: 'team' as const })),
        ...(r.users || []).map((u) => ({ ...u, kind: 'user' as const })),
      ]))
      .catch(() => {});
  }, []);

  // Auto-crecer: la caja se agranda con el contenido para VER todo lo que se escribe
  // (hasta un tope alto, después scrollea). Se recalcula en cada cambio de texto.
  useEffect(() => {
    const el = ref.current; if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(minHeight, el.scrollHeight), 320)}px`;
  }, [value, minHeight]);

  const suggestions = open
    ? targets.filter((m) => m.name.toLowerCase().includes(query.toLowerCase())).slice(0, 8)
    : [];

  // Detecta el token "@..." justo antes del cursor.
  function refresh(el: HTMLTextAreaElement) {
    const upto = el.value.slice(0, el.selectionStart ?? el.value.length);
    const m = /@([^@\n]{0,40})$/.exec(upto);
    if (m) { setQuery(m[1]); setSel(0); setOpen(true); } else setOpen(false);
  }

  function pick(name: string) {
    const el = ref.current; if (!el) return;
    const pos = el.selectionStart ?? value.length;
    const before = value.slice(0, pos).replace(/@([^@\n]{0,40})$/, `@${name} `);
    const next = before + value.slice(pos);
    onChange(next);
    setOpen(false);
    // Recoloca el cursor tras el nombre insertado.
    requestAnimationFrame(() => { el.focus(); const p = before.length; el.setSelectionRange(p, p); });
  }

  return (
    <div style={{ position: 'relative' }}>
      <textarea
        ref={ref}
        className="input"
        style={{ width: '100%', minHeight, resize: 'vertical', overflow: 'auto', ...style }}
        placeholder={placeholder}
        value={value}
        onPaste={onPaste}
        onChange={(e) => { onChange(e.target.value); refresh(e.target); }}
        onKeyDown={(e) => {
          if (open && suggestions.length > 0) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => (s + 1) % suggestions.length); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => (s - 1 + suggestions.length) % suggestions.length); }
            else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(suggestions[sel].name); }
            else if (e.key === 'Escape') setOpen(false);
            return;
          }
          // `e.repeat` = tecla apretada y repitiendo (~11/s). Sin este guard, dejar el Enter
          // apretado dispara un envío por repetición — pasó en el chat del claim el 2026-08-06.
          if (onEnter && e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (!e.repeat) onEnter(); }
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {/* Los botones de IA (mejorar / traducir al inglés), flotando a la izquierda del handle
          de resize. El posicionamiento lo pone el propio componente. */}
      <ImproveTextButton value={value} onChange={onChange} iconOnly />
      {open && suggestions.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 20, left: 6, top: '100%', minWidth: 200, background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,.12)', overflow: 'hidden' }}>
          {suggestions.map((m, i) => (
            <div key={`${m.kind}-${m.id}`} onMouseDown={(e) => { e.preventDefault(); pick(m.name); }}
              style={{ padding: '6px 10px', fontSize: 'var(--fs-sm)', cursor: 'pointer', background: i === sel ? 'var(--surface-2)' : 'transparent', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-flex' }}>{m.kind === 'team' ? <Users size={13} strokeWidth={1.75} /> : m.kind === 'department' ? <Building2 size={13} strokeWidth={1.75} /> : '@'}</span>{m.name}{m.kind === 'team' && <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--muted)' }}>team</span>}{m.kind === 'department' && <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--muted)' }}>dept</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
